import { Plus, Trash2, ArrowRight } from "lucide-react";

/**
 * Visual builder for branching scenarios.
 * Value shape: [{ id, prompt, options: [{ id, text, score, next }] }]
 * "next" is a node id or "END".
 */
export default function BranchBuilder({ value = [], onChange }) {
  const nodes = value;

  const update = (idx, patch) => {
    const next = nodes.map((n, i) => i === idx ? { ...n, ...patch } : n);
    onChange(next);
  };

  const addNode = () => {
    const id = `n${nodes.length + 1}`;
    onChange([...nodes, { id, prompt: "", options: [] }]);
  };

  const removeNode = (idx) => onChange(nodes.filter((_, i) => i !== idx));

  const addOption = (nIdx) => {
    const optCount = nodes[nIdx].options?.length || 0;
    const optId = String.fromCharCode(97 + optCount); // a, b, c...
    update(nIdx, { options: [...(nodes[nIdx].options || []), { id: optId, text: "", score: 5, next: "END" }] });
  };

  const updateOption = (nIdx, oIdx, patch) => {
    const opts = nodes[nIdx].options.map((o, i) => i === oIdx ? { ...o, ...patch } : o);
    update(nIdx, { options: opts });
  };

  const removeOption = (nIdx, oIdx) => {
    update(nIdx, { options: nodes[nIdx].options.filter((_, i) => i !== oIdx) });
  };

  const nodeChoices = ["END", ...nodes.map(n => n.id)];
  const input = "w-full bg-white border hairline px-2 py-1.5 text-sm focus:outline-none focus:border-[color:var(--brand-primary)]";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">Branch Tree ({nodes.length} node{nodes.length===1?"":"s"})</div>
        <button type="button" onClick={addNode} data-testid="branch-add-node" className="btn-ghost text-xs flex items-center gap-1">
          <Plus size={12}/> Add node
        </button>
      </div>

      {nodes.map((node, nIdx) => (
        <div key={nIdx} className="border hairline bg-[color:var(--brand-surface2)]/40 p-4 space-y-3" data-testid={`branch-node-${nIdx}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="label-eyebrow">Node</div>
              <input
                className={`${input} w-24 font-mono`}
                value={node.id}
                onChange={e => update(nIdx, { id: e.target.value.replace(/\s/g, "") || `n${nIdx+1}` })}
                data-testid={`branch-node-id-${nIdx}`}
                placeholder="id"
              />
              {nIdx === 0 && <span className="label-eyebrow text-[var(--brand-primary)]">← start (first node is entry point; use id "start" by convention)</span>}
            </div>
            <button type="button" onClick={()=>removeNode(nIdx)} data-testid={`branch-node-del-${nIdx}`} className="text-neutral-500 hover:text-black">
              <Trash2 size={14}/>
            </button>
          </div>

          <textarea
            rows={2}
            className={input}
            value={node.prompt}
            onChange={e => update(nIdx, { prompt: e.target.value })}
            placeholder="Prospect prompt shown to the trainee..."
            data-testid={`branch-node-prompt-${nIdx}`}
          />

          <div className="space-y-2 pl-4 border-l-2 border-[color:var(--brand-primary)]">
            {(node.options || []).map((opt, oIdx) => (
              <div key={oIdx} className="grid grid-cols-12 gap-2 items-center" data-testid={`branch-opt-row-${nIdx}-${oIdx}`}>
                <input
                  className={`${input} col-span-1 font-mono uppercase`}
                  value={opt.id}
                  onChange={e => updateOption(nIdx, oIdx, { id: e.target.value.slice(0,3).toLowerCase() })}
                  data-testid={`branch-opt-id-${nIdx}-${oIdx}`}
                />
                <input
                  className={`${input} col-span-6`}
                  value={opt.text}
                  onChange={e => updateOption(nIdx, oIdx, { text: e.target.value })}
                  placeholder="Trainee response option..."
                  data-testid={`branch-opt-text-${nIdx}-${oIdx}`}
                />
                <input
                  type="number" min={0} max={10}
                  className={`${input} col-span-1 text-center`}
                  value={opt.score}
                  onChange={e => updateOption(nIdx, oIdx, { score: Number(e.target.value) })}
                  title="Score 0-10"
                  data-testid={`branch-opt-score-${nIdx}-${oIdx}`}
                />
                <div className="col-span-3 flex items-center gap-1">
                  <ArrowRight size={12} className="text-neutral-500"/>
                  <select
                    className={input}
                    value={opt.next || "END"}
                    onChange={e => updateOption(nIdx, oIdx, { next: e.target.value })}
                    data-testid={`branch-opt-next-${nIdx}-${oIdx}`}
                  >
                    {nodeChoices.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <button type="button" onClick={()=>removeOption(nIdx, oIdx)} className="col-span-1 text-neutral-500 hover:text-black flex justify-center">
                  <Trash2 size={12}/>
                </button>
              </div>
            ))}
            <button type="button" onClick={()=>addOption(nIdx)} data-testid={`branch-add-opt-${nIdx}`} className="text-xs text-[var(--brand-primary)] link-underline flex items-center gap-1">
              <Plus size={10}/> Add option
            </button>
          </div>
        </div>
      ))}

      {nodes.length === 0 && (
        <div className="text-sm text-neutral-500 p-6 border hairline text-center bg-white">
          No nodes yet. Click <strong>Add node</strong> to start building the branching drill.
          The first node should be named <code className="font-mono">start</code>.
        </div>
      )}
    </div>
  );
}
